import type { Product } from '@/lib/productService';

export type ExperiencePreviewProduct = {
  id: string;
  name: string;
  imageUrl?: string | null;
  price: number;
  promotionPrice?: number;
  catalogType?: 'GROCERIES' | 'FASHION' | 'FOOD';
  isPlaceholder?: boolean;
};

const MIN_PRODUCTS = 4;
const MAX_PRODUCTS = 8;
const MAX_NAME_LENGTH = 40;

function isProductSellable(product: Product): boolean {
  if (!product.active) return false;
  if (product.unlimitedStock) return true;
  if (product.variants && product.variants.length > 0) {
    return product.variants.some((variant) => variant.stock > 0);
  }
  return product.stock > 0;
}

function toPreviewProduct(product: Product): ExperiencePreviewProduct {
  return {
    id: product.id,
    name: product.name,
    imageUrl: product.imageUrl,
    price: product.price,
    promotionPrice: product.promotionPrice,
    catalogType: product.catalogType,
  };
}

function createPlaceholders(count: number): ExperiencePreviewProduct[] {
  return Array.from({ length: count }, (_, index) => ({
    id: `placeholder-${index}`,
    name: '',
    price: 0,
    catalogType: 'FASHION',
    isPlaceholder: true,
  }));
}

function padProducts(
  products: ExperiencePreviewProduct[],
  min: number
): ExperiencePreviewProduct[] {
  if (products.length === 0) {
    return createPlaceholders(min);
  }
  if (products.length >= min) {
    return products;
  }

  const result = [...products];
  let index = 0;
  while (result.length < min) {
    const source = products[index % products.length];
    result.push({
      ...source,
      id: `${source.id}-dup-${result.length}`,
    });
    index += 1;
  }
  return result;
}

/** Pick a small deck of products for the catalog Experience Feed preview animation. */
export function selectExperiencePreviewProducts(
  products: Product[]
): ExperiencePreviewProduct[] {
  const seen = new Set<string>();
  const eligible: Product[] = [];

  for (const product of products) {
    if (seen.has(product.id)) continue;
    seen.add(product.id);
    if (!isProductSellable(product)) continue;
    if (product.price <= 0) continue;
    if (product.name.length > MAX_NAME_LENGTH) continue;
    eligible.push(product);
  }

  eligible.sort((left, right) => {
    const leftHasImage = left.imageUrl?.trim() ? 1 : 0;
    const rightHasImage = right.imageUrl?.trim() ? 1 : 0;
    if (rightHasImage !== leftHasImage) {
      return rightHasImage - leftHasImage;
    }
    return left.name.length - right.name.length;
  });

  const selected = eligible.slice(0, MAX_PRODUCTS).map(toPreviewProduct);
  return padProducts(selected, MIN_PRODUCTS);
}
